/**
 * Parser dei titoli cartellone: estrae concessionaria, voto (V1-V9) e tag
 * dalle convenzioni usate nei titoli, es. "ALESSI 3219 ( SCUOLA ) V4",
 * "DAMIR NO CIMASA FRONTE ELENKA V8", "ASTCOM CIM 855 V6".
 * Nessun campo nuovo a DB: tutto derivato, tollerante ai titoli fuori schema.
 */

/** Concessionari di Palermo usati nei titoli fin dall'inizio: riconosciuti anche se non
 *  (ancora) creati come aziende. */
const BASE = ['ALESSI', 'DAMIR', 'START', 'ASTCOM', 'FAP', 'SPA', 'COMUNE DI PALERMO'];
const alfabetico = (a, b) => a.localeCompare(b, 'it');
// per il confronto i nomi più lunghi vanno prima: "COMUNE DI PALERMO" prima di un eventuale "COMUNE"
let perConfronto = [...BASE].sort((a, b) => b.length - a.length || alfabetico(a, b));
/** Impianto senza concessionaria: né assegnata nel database né scritta nel titolo. */
export const IGNOTA = 'IGNOTA';
/** Elenco per i filtri, in ordine alfabetico, con IGNOTA in fondo (per trovare quelli da
 *  sistemare). Cresce con impostaConcessionarie(). */
export let CONCESSIONARIE = [...[...BASE].sort(alfabetico), IGNOTA];

/**
 * Aggiunge ai concessionari di base quelli creati in "Aziende e utenti" (anche senza
 * utenti): così si inseriscono piano piano tutti quelli della Sicilia senza toccare il codice.
 */
export function impostaConcessionarie(nomi = []) {
  const tutti = [...new Set([...BASE, ...nomi.map(n => String(n).trim().toUpperCase()).filter(Boolean)])];
  perConfronto = [...tutti].sort((a, b) => b.length - a.length || alfabetico(a, b));
  CONCESSIONARIE = [...tutti.sort(alfabetico), IGNOTA];
}

const TAG_PATTERNS = [
  { tag: 'NO CIMASA',  re: /\bNO\s+CIMASA\b/i },
  { tag: 'CIMASA',     re: /\bCIM(ASA)?\b/i },
  { tag: 'LEDWALL',    re: /\bLED\s?WALL\b/i },
  { tag: 'ROTORLED',   re: /\bROTORLED\b/i },
  { tag: 'DIGITALE',   re: /\bDIGITALE\b/i },
  { tag: 'ILLUMINATO', re: /\bILLUMINAT[OA]\b/i },
  { tag: '6x3',        re: /\b6\s?[xX×]\s?3\b/ },
  { tag: '200x140',    re: /\b200\s?[xX×]\s?140\b/ },
  { tag: '200x150',    re: /\b200\s?[xX×]\s?150\b/ },
];

export function parseCartellone(item) {
  const title = (item?.title || '').trim();
  const up = title.toUpperCase();

  // La concessionaria è l'azienda assegnata nel nostro database; se l'impianto non è
  // assegnato, si legge dal titolo, come parola intera ("ALESSI 3219" sì, "Alessio 6x3" no).
  // Né l'una né l'altra: IGNOTA.
  const concessionaria = item?.azienda ? String(item.azienda).toUpperCase()
    : perConfronto.find(c => up.startsWith(c) && !/\p{L}/u.test(up.charAt(c.length))) || IGNOTA;

  // voto: vince il campo a DB se valorizzato; altrimenti si deduce dal titolo,
  // dove può comparire più di un voto ("V7 V9") → si espone il migliore
  const voti = [...up.matchAll(/\bV([1-9])\b/g)].map(m => Number(m[1]));
  const votoTitolo = voti.length ? Math.max(...voti) : null;
  const voto = item?.voto != null ? Number(item.voto) : votoTitolo;

  const tags = [];
  for (const { tag, re } of TAG_PATTERNS) {
    if (re.test(title)) {
      if (tag === 'CIMASA' && tags.includes('NO CIMASA')) continue; // "NO CIMASA" vince
      tags.push(tag);
    }
  }

  // "revisionato" = tutti i dati inseriti: titolo, via/numero, link Street View, voto e
  // concessionaria. "da rivedere" = manca almeno uno (inserimento non ancora completato).
  const has = (v) => v != null && String(v).trim() !== '';
  const completo = has(item?.title) && has(item?.nomination) && has(item?.urlstreeview) && voto != null
                   && concessionaria !== IGNOTA;
  const stato = completo ? 'revisionato' : 'da rivedere';

  return { concessionaria, voto, voti, tags, stato };
}

/**
 * Titolo con il voto scelto dai pulsanti V1-V9, perché titolo e voto non si contraddicano:
 * un solo "Vn" si sostituisce dov'è; nessuno si aggiunge in fondo; più d'uno ("V7 V9")
 * diventano quello scelto, in fondo. Senza voto i "Vn" si tolgono.
 */
export function titoloConVoto(title, voto) {
  const t = (title || '').trim();
  const senza = () => t.replace(/\s*\bV[1-9]\b/gi, '').replace(/\s{2,}/g, ' ').trim();
  if (!voto) return senza();
  const quanti = (t.match(/\bV[1-9]\b/gi) || []).length;
  if (quanti === 1) return t.replace(/\bV[1-9]\b/i, `V${voto}`);
  return `${senza()} V${voto}`.trim();
}

/** Voto scritto nel titolo (il migliore, se sono più d'uno), oppure null. */
export function votoDalTitolo(title) {
  const v = [...String(title || '').matchAll(/\bV([1-9])\b/gi)].map(m => Number(m[1]));
  return v.length ? Math.max(...v) : null;
}

/** Etichetta del voto per la UI: "V6" oppure "V?" quando non indicato. */
export function votoLabel(voto) {
  return voto ? `V${voto}` : 'V?';
}

/** Ritorna l'URL solo se è http/https (blocca javascript:/data: → anti-XSS); altrimenti null. */
export function safeHttpUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(String(url).trim());
    return (u.protocol === 'http:' || u.protocol === 'https:') ? u.href : null;
  } catch { return null; }
}

/** URL Street View del cartellone: link salvato se valido, altrimenti fallback dalle coordinate. */
export function streetViewUrl(raw) {
  return safeHttpUrl(raw?.urlstreeview)
    || `https://maps.google.com/maps?q=&layer=c&cbll=${raw?.latitude},${raw?.longitude}`;
}

/** Colore del badge voto: V1-3 rosso, V4-5 arancio, V6-7 blu, V8-9 verde scuro. */
export function votoColor(voto) {
  if (voto == null) return { bg: '#e2e8f0', fg: '#475569' };
  if (voto <= 3) return { bg: '#fee2e2', fg: '#b91c1c' };
  if (voto <= 5) return { bg: '#ffedd5', fg: '#c2410c' };
  if (voto <= 7) return { bg: '#dbeafe', fg: '#1d4ed8' };
  return { bg: '#1e3a8a', fg: '#ffffff' };
}

/** Escape dei metacaratteri regex: rende sicura la ricerca da input utente (es. "?", "(", "["). */
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Applica ricerca + filtri; ogni item è {raw, meta} (cartellone + parse). */
export function filterItems(items, { search, concessionarie, votoMin, stato, tags }) {
  const q = (search || '').trim();
  // ogni parola separata da spazio deve comparire nell'ordine ("ALESSI SCUOLA" → /ALESSI.+SCUOLA/i);
  // i metacaratteri sono escapati, quindi cercare "?" o "(" non fa più crashare l'app.
  let re = null;
  if (q) {
    try { re = new RegExp(q.split(/\s+/).map(escapeRegExp).join('.+'), 'i'); }
    catch { re = null; }
  }
  return items.filter(({ raw, meta }) => {
    if (re) {
      // cercabile anche per numero/id: "872", "id 872" e la forma a 7 cifre "0000872"
      const hay = [raw.title, raw.description, raw.nomination,
                   `id ${raw.id}`, String(raw.id).padStart(7, '0')].filter(Boolean).join(' | ');
      if (!re.test(hay)) return false;
    }
    if (concessionarie?.length && !concessionarie.includes(meta.concessionaria)) return false;
    if (votoMin != null && (meta.voto == null || meta.voto < votoMin)) return false;
    if (stato && meta.stato !== stato) return false;
    if (tags?.length && !tags.every(t => meta.tags.includes(t))) return false;
    return true;
  });
}

export const SORTS = {
  'voto-desc':  { label: 'VOTO decrescente',  fn: (a, b) => (b.meta.voto ?? -1) - (a.meta.voto ?? -1) },
  'voto-asc':   { label: 'VOTO crescente',    fn: (a, b) => (a.meta.voto ?? 10) - (b.meta.voto ?? 10) },
  'title':      { label: 'Titolo A→Z',        fn: (a, b) => (a.raw.title || '').localeCompare(b.raw.title || '') },
  'recenti':    { label: 'Più recenti',       fn: (a, b) => (b.raw.creationdate ?? 0) - (a.raw.creationdate ?? 0) },
  'da-rivedere':{ label: 'Da rivedere prima', fn: (a, b) => (a.raw.dataChange == null ? 0 : 1) - (b.raw.dataChange == null ? 0 : 1) },
  'vicini':     { label: 'Più vicini a me',   fn: (a, b) => a.raw.id - b.raw.id /* sostituito a runtime con la distanza GPS */ },
};

/** Distanza in metri tra due coordinate (haversine). */
export function distanceM(lat1, lon1, lat2, lon2) {
  if ([lat1, lon1, lat2, lon2].some(v => v == null)) return Infinity;
  const R = 6371000, toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export function formatDist(m) {
  if (!isFinite(m)) return '';
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
}
